import { isIntranetHtmlBody } from '@gb-ticket/shared-types';
import { type KeyboardEvent, type MouseEvent, useLayoutEffect, useRef, useState } from 'react';

import { IntranetImageLightbox } from '@/products/intranet/components/intranet-image-frame';
import { useColleagueProfile } from '@/products/intranet/features/intranet/colleague-profile-context';
import { frameIntranetBodyImages } from '@/products/intranet/features/intranet/image-frame';

/**
 * Pasted mail often joins words with non-breaking spaces. Those spaces do not wrap,
 * so a line breaks inside a word to stay in the column. Normal spaces wrap between words.
 */
function wrapOnSpaces(value: string): string {
  return value.replace(/&nbsp;|&#160;|&#xa0;/gi, ' ').replaceAll('\u00A0', ' ');
}

/**
 * The one renderer for content bodies. Editor bodies are HTML the server has already reduced to its
 * allowlist; older bodies without a leading tag stay plain text, so their markup is never parsed.
 * Mentions (`span[data-user-id]`) open the colleague sheet; they are not links.
 * Images stay inside the 680 preview. Only an article (`expandImages`) opens the original.
 */
export function IntranetBody({
  body,
  expandImages = false,
}: {
  body: string | null;
  expandImages?: boolean;
}) {
  const { open } = useColleagueProfile();
  const rootRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState<{ src: string; alt: string } | null>(null);
  const readable = body ? wrapOnSpaces(body) : body;
  const html = readable && isIntranetHtmlBody(readable) ? readable : null;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || !html) {
      return;
    }
    return frameIntranetBodyImages(root, expandImages);
  }, [html, expandImages]);

  if (!body?.trim()) {
    return null;
  }

  function openPhoto(photo: HTMLImageElement) {
    setZoom({ src: photo.currentSrc || photo.src, alt: photo.alt });
  }

  function onClick(event: MouseEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    const mention = target.closest('span[data-user-id]');
    if (mention instanceof HTMLElement) {
      const userId = mention.getAttribute('data-user-id')?.trim();
      if (!userId) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      open(userId);
      return;
    }
    if (!expandImages) {
      return;
    }
    const frame = target.closest('.intranet-image-frame');
    const photo = frame?.querySelector('img.intranet-image-photo');
    if (!(photo instanceof HTMLImageElement)) {
      return;
    }
    event.preventDefault();
    openPhoto(photo);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (!expandImages || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }
    const target = event.target;
    if (
      !(target instanceof HTMLImageElement) ||
      !target.classList.contains('intranet-image-photo')
    ) {
      return;
    }
    event.preventDefault();
    openPhoto(target);
  }

  if (html) {
    return (
      <>
        <div
          ref={rootRef}
          className={
            expandImages
              ? 'intranet-prose intranet-image-expand text-base leading-relaxed'
              : 'intranet-prose text-base leading-relaxed'
          }
          dangerouslySetInnerHTML={{ __html: html }}
          onClick={onClick}
          onKeyDown={onKeyDown}
        />
        {zoom ? (
          <IntranetImageLightbox src={zoom.src} alt={zoom.alt} onClose={() => setZoom(null)} />
        ) : null}
      </>
    );
  }
  return <div className="text-base leading-relaxed whitespace-pre-wrap">{readable}</div>;
}
